"use client";
import { UserStatusCode } from "../services/user-service";
import { Menu } from "@headlessui/react";
import EmptyState from "../../../components/custom/empty-state";

export interface UserRow {
    id: number;
    username: string;            // mã nhân viên
    name: string;            // tên nhân viên
    email: string;
    phone: string;
    department_id?: number;    // id bộ phận
    department_name?: string; // tên bộ phận
    birthday?: string;       // ngày sinh
    joining_date?: string;      // ngày vào làm
    privilege_group_id?: number;
    privilege_group_name?: string;
    gender?: number;
    address?: string;
    avatar?: string;
    id_card_number?: string;
    customer_ids?: string;
    is_driver?: number;      // 1 nếu có quyền DRIVER
    is_counter?: number;     // 1 nếu có quyền COUNTING_STAFF
    is_all_branch?: number;  // 1 = thuộc tất cả chi nhánh
    branches?: { id: number; name: string }[]; // chi nhánh NV trực thuộc
    created_at: string;      // ngày tạo
    statusCode: UserStatusCode;
  }
  

interface UserTableProps {
  rows: UserRow[];
  isLoading: boolean;
  startIndex: number;
  onEdit?: (row: UserRow) => void;
  onDeactivate?: (row: UserRow) => void;
  onActivate?: (row: UserRow) => void;
  onViewDetail?: (row: UserRow) => void;
  onResetPassword?: (row: UserRow) => void;
  onAssignCustomer?: (row: UserRow) => void;
}

/** Số khách hàng user đang phụ trách — đếm từ CSV `customer_ids` (bỏ trùng, bỏ rỗng). */
const countAssignedCustomers = (csv?: string): number =>
  csv
    ? new Set(
        csv
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      ).size
    : 0;

const UserTable: React.FC<UserTableProps> = ({
  rows,
  isLoading,
  startIndex,
  onEdit,
  onDeactivate,
  onActivate,
  onViewDetail,
  onResetPassword,
  onAssignCustomer,
}) => {
  if (isLoading) return <div className="p-4 text-gray-500">Đang tải...</div>;
  if (!rows.length) return <EmptyState message="Chưa có nhân viên" description="Không tìm thấy nhân viên khớp bộ lọc." />;

  return (
    <table className="min-w-full border text-sm">
    <thead className="bg-gray-100">
        <tr>
        <th className="border px-3 py-2 text-center">STT</th>
        <th className="border px-3 py-2 text-center">Mã nhân viên</th>
        <th className="border px-3 py-2 text-center">Tên nhân viên</th>
        <th className="border px-3 py-2 text-center">Bộ phận</th>
        <th className="border px-3 py-2 text-center">Chi nhánh</th>
        <th className="border px-3 py-2 text-center">Khách phụ trách</th>
        <th className="border px-3 py-2 text-center">Ngày sinh</th>
        <th className="border px-3 py-2 text-center">Ngày vào làm</th>
        <th className="border px-3 py-2 text-center">Thao tác</th>
        </tr>
    </thead>
    <tbody>
        {rows.map((row, idx) => (
        <tr key={row.id} className="hover:bg-gray-50 group">
            <td className="border px-3 py-2 text-center">{startIndex + idx}</td>
            <td className="border px-3 py-2">{row.username}</td>
            <td className="border px-3 py-2">{row.name}</td>
            <td className="border px-3 py-2">{row.department_name || "—"}</td>
            <td className="border px-3 py-2">
              {row.is_all_branch === 1 ? (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-bold text-green-700">
                  Tất cả chi nhánh
                </span>
              ) : row.branches && row.branches.length > 0 ? (
                row.branches.map((b) => b.name).join(", ")
              ) : (
                "—"
              )}
            </td>
            <td className="border px-3 py-2 text-center">
              {row.is_driver === 1 || row.is_counter === 1 ? (
                (() => {
                  const n = countAssignedCustomers(row.customer_ids);
                  return (
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                        n > 0 ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {n} khách
                    </span>
                  );
                })()
              ) : (
                "—"
              )}
            </td>
            <td className="border px-3 py-2">{row.birthday || "—"}</td>
            <td className="border px-3 py-2">{row.joining_date || "—"}</td>
            <td className="border px-3 py-2 text-center">
              <Menu as="div" className="relative inline-block text-left">
                <div>
                  <Menu.Button className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-slate-500 mx-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                    </svg>
                  </Menu.Button>
                </div>
                
                  {/* anchor: Headless UI v2 tự tính vị trí + PORTAL ra body + auto-flip khi thiếu chỗ
                      (row cuối bảng không còn bị cắt). `transition` thay cho <Transition> bọc ngoài. */}
                  <Menu.Items
                    transition
                    anchor="bottom end"
                    className="z-[60] w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden [--anchor-gap:0.5rem] [--anchor-max-height:70vh] transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
                  >

                    {onViewDetail && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => onViewDetail(row)}
                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} group flex w-full items-center px-4 py-2 text-sm`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"/>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                            Xem chi tiết
                          </button>
                        )}
                      </Menu.Item>
                    )}

                    {onEdit && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => onEdit(row)}
                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} group flex w-full items-center px-4 py-2 text-sm`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652l-9.547 9.546a4.5 4.5 0 01-1.897 1.13l-3.034.909.91-3.034a4.5 4.5 0 011.129-1.897l9.547-9.546z"/>
                            </svg>
                            Sửa
                          </button>
                        )}
                      </Menu.Item>
                    )}

                    {onAssignCustomer && (row.is_driver === 1 || row.is_counter === 1) && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => onAssignCustomer(row)}
                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} group flex w-full items-center px-4 py-2 text-sm`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                            </svg>
                            Gán Khách hàng
                          </button>
                        )}
                      </Menu.Item>
                    )}

                    {onResetPassword && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => onResetPassword(row)}
                            className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} group flex w-full items-center px-4 py-2 text-sm`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v-2.25l1.159-1.159c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                            </svg>
                            Reset Mật khẩu
                          </button>
                        )}
                      </Menu.Item>
                    )}

                    {row.statusCode === UserStatusCode.ACTIVE && onDeactivate && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => onDeactivate(row)}
                            className={`${active ? 'bg-red-50 text-red-700' : 'text-red-600'} group flex w-full items-center px-4 py-2 text-sm border-t border-gray-100`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="mr-3 h-5 w-5 text-red-500 group-hover:text-red-600">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75h4.5v4.5h-4.5z"/>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21.75c5.385 0 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25 2.25 6.615 2.25 12 6.615 21.75 12 21.75z"/>
                            </svg>
                            Ngưng hoạt động
                          </button>
                        )}
                      </Menu.Item>
                    )}

                    {row.statusCode === UserStatusCode.INACTIVE && onActivate && (
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => onActivate(row)}
                            className={`${active ? 'bg-green-50 text-green-700' : 'text-green-600'} group flex w-full items-center px-4 py-2 text-sm border-t border-gray-100`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="mr-3 h-5 w-5 text-green-500 group-hover:text-green-600">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                            </svg>
                            Kích hoạt
                          </button>
                        )}
                      </Menu.Item>
                    )}
                  </Menu.Items>
              </Menu>
            </td>



        </tr>
        ))}
    </tbody>
    </table>
  );
};

export default UserTable;
